// Jul-2026 (User-Wunsch) — Direkt-Scan vom Arbeitsplatz-Scanner:
// verbindet den lokalen Scan-Ordner (File System Access API, Chrome/Edge)
// EINMALIG und wartet danach automatisch auf die nächste neue Scan-Datei.
const DB_NAME = "tw_scan_folder";
const STORE = "handles";

function idb() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(STORE);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function idbGet(key) {
    const db = await idb();
    return new Promise((res, rej) => {
        const t = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
        t.onsuccess = () => res(t.result);
        t.onerror = () => rej(t.error);
    });
}

async function idbSet(key, val) {
    const db = await idb();
    return new Promise((res, rej) => {
        const t = db.transaction(STORE, "readwrite").objectStore(STORE).put(val, key);
        t.onsuccess = () => res();
        t.onerror = () => rej(t.error);
    });
}

export const scanFolderSupported = () =>
    typeof window !== "undefined" && "showDirectoryPicker" in window;

export async function getConnectedFolderName() {
    const handle = await idbGet("scan_folder").catch(() => null);
    return handle?.name || null;
}

export async function getScanFolder() {
    let handle = await idbGet("scan_folder").catch(() => null);
    if (handle) {
        try {
            const perm = await handle.queryPermission({ mode: "read" });
            if (perm === "granted") return handle;
            if ((await handle.requestPermission({ mode: "read" })) === "granted") return handle;
        } catch { /* Handle ungültig → neu wählen */ }
    }
    handle = await window.showDirectoryPicker({ id: "tw-scan-folder", mode: "read" });
    await idbSet("scan_folder", handle).catch(() => {});
    return handle;
}

const SCAN_EXT = /\.(pdf|jpe?g|png|tiff?|bmp|webp)$/i;

export async function waitForNewScan(handle, { sinceMs, timeoutMs = 90000, signal } = {}) {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        if (signal?.aborted) return null;
        for await (const entry of handle.values()) {
            if (entry.kind !== "file" || !SCAN_EXT.test(entry.name)) continue;
            const file = await entry.getFile();
            if (file.lastModified >= sinceMs && file.size > 0) return file;
        }
        await new Promise((r) => setTimeout(r, 1500));
    }
    return null;
}

// ============================================================================
// Jul-2026 (User-Wunsch) — Dokumenten-Postausgang: überwachter Desktop-Ordner
// mit SCHREIB-Rechten (Dateien werden nach Verarbeitung gelöscht).
// ============================================================================
const OUTBOX_EXT = /\.(pdf|jpe?g|png|tiff?|bmp|webp)$/i;

export async function getOutboxFolderName() {
    const handle = await idbGet("outbox_folder").catch(() => null);
    return handle?.name || null;
}

export async function getOutboxFolder(forcePick = false) {
    let handle = forcePick ? null : await idbGet("outbox_folder").catch(() => null);
    if (handle) {
        try {
            const perm = await handle.queryPermission({ mode: "readwrite" });
            if (perm === "granted") return handle;
            if ((await handle.requestPermission({ mode: "readwrite" })) === "granted") return handle;
        } catch { /* Handle ungültig → neu wählen */ }
    }
    handle = await window.showDirectoryPicker({ id: "tw-outbox-folder", mode: "readwrite" });
    await idbSet("outbox_folder", handle).catch(() => {});
    return handle;
}

export async function listOutboxFiles(handle) {
    const out = [];
    for await (const entry of handle.values()) {
        if (entry.kind !== "file" || !OUTBOX_EXT.test(entry.name)) continue;
        try {
            const file = await entry.getFile();
            if (file.size > 0) out.push(file);
        } catch { /* Datei ggf. gerade in Bearbeitung */ }
    }
    return out;
}

export async function deleteOutboxFile(handle, name) {
    try { await handle.removeEntry(name); return true; } catch { return false; }
}

// ============================================================================
// Jul-2026 (User-Wunsch) — Dokumenten-Posteingang: eigener überwachter Ordner
// für Lieferanten-Belege (Lieferscheine/Rechnungen), Schreibrechte zum Löschen.
// ============================================================================
export async function getInboxFolderName() {
    const handle = await idbGet("inbox_folder").catch(() => null);
    return handle?.name || null;
}

export async function getInboxFolder(forcePick = false) {
    let handle = forcePick ? null : await idbGet("inbox_folder").catch(() => null);
    if (handle) {
        try {
            const perm = await handle.queryPermission({ mode: "readwrite" });
            if (perm === "granted") return handle;
            if ((await handle.requestPermission({ mode: "readwrite" })) === "granted") return handle;
        } catch { /* Handle ungültig → neu wählen */ }
    }
    handle = await window.showDirectoryPicker({ id: "tw-inbox-folder", mode: "readwrite" });
    await idbSet("inbox_folder", handle).catch(() => {});
    return handle;
}

// Jul-2026 (User-Wunsch): Überwachung standardmäßig aktiv — gemerkten Ordner
// OHNE Nutzer-Geste wieder verbinden, wenn die Rechte noch bestehen.
async function _storedGranted(key) {
    const handle = await idbGet(key).catch(() => null);
    if (!handle) return null;
    try {
        if ((await handle.queryPermission({ mode: "readwrite" })) === "granted") return handle;
    } catch { /* Handle ungültig */ }
    return { needsPermission: true, name: handle.name };
}

export const getStoredOutboxFolder = () => _storedGranted("outbox_folder");
export const getStoredInboxFolder = () => _storedGranted("inbox_folder");

// ============================================================================
// Aug-2026 (User-Wunsch) — Dokumenten-Center: EIN überwachter Ordner für alle
// Dokumente. Migration: gemerkte Posteingang-/Postausgang-Ordner werden
// automatisch übernommen, damit niemand neu auswählen muss.
// ============================================================================
export async function getDocCenterFolder(forcePick = false) {
    let handle = forcePick ? null : await idbGet("doccenter_folder").catch(() => null);
    if (!handle && !forcePick) {
        handle = (await idbGet("inbox_folder").catch(() => null))
            || (await idbGet("outbox_folder").catch(() => null));
    }
    if (handle) {
        try {
            const perm = await handle.queryPermission({ mode: "readwrite" });
            if (perm === "granted" || (await handle.requestPermission({ mode: "readwrite" })) === "granted") {
                await idbSet("doccenter_folder", handle).catch(() => {});
                return handle;
            }
        } catch { /* Handle ungültig → neu wählen */ }
    }
    handle = await window.showDirectoryPicker({ id: "tw-doccenter-folder", mode: "readwrite" });
    await idbSet("doccenter_folder", handle).catch(() => {});
    return handle;
}

export async function getStoredDocCenterFolder() {
    const keys = ["doccenter_folder", "inbox_folder", "outbox_folder"];
    let needs = null;
    for (const key of keys) {
        // eslint-disable-next-line no-await-in-loop
        const h = await _storedGranted(key);
        if (h && !h.needsPermission) {
            await idbSet("doccenter_folder", h).catch(() => {});
            return h;
        }
        if (h && !needs) needs = h;
    }
    return needs;
}
